import React from "react";
import { Paperclip, Eye, Download, Trash2, Loader2 } from "lucide-react";
import { useAppSelector } from "../../redux/hooks";
import { File, Status } from "../../redux/interfaces/folder.types";
import { parseDateToSpanishString } from "../../utils/common/parseDateToSpanishString";
import { resolveScopedVaultApi } from "../../api/vault.api";
import { useRemoveFile } from "../../hooks/vault/mutations/useRemoveFile";
import { useDownloadFile } from "../../hooks/vault/mutations/useDownloadFile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const validDeleteStatus = [Status.PendingUpload, Status.Invalid];

interface FileItemProps {
  file: File;
  showDelete: boolean;
  onRemove: () => void;
}

const FileItem: React.FC<FileItemProps> = ({ file, showDelete, onRemove }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const downloadFileMutation = useDownloadFile();
  const scopedVaultApi = resolveScopedVaultApi(
    typeof window !== "undefined" ? window.location.pathname : "",
  );

  const onViewFile = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const blob = await scopedVaultApi.downloadFile(file.id);

      const fileExtension = file.filename.split(".").pop()?.toLowerCase();
      const mimeTypes: Record<string, string> = {
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        webp: "image/webp",
        pdf: "application/pdf",
      };

      const mimeType =
        fileExtension && mimeTypes[fileExtension]
          ? mimeTypes[fileExtension]
          : "application/octet-stream";

      const typedBlob = new Blob([blob], { type: mimeType });
      const url = URL.createObjectURL(typedBlob);
      window.open(url, "_blank");

      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch {
      setError("Error al abrir el archivo");
    } finally {
      setIsLoading(false);
    }
  };

  const onDownloadFile = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await downloadFileMutation.mutateAsync({
        fileId: file.id,
        filename: file.filename,
      });
    } catch {
      setError("Error al descargar");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/50 p-3 transition-colors hover:bg-gray-50">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
        <Paperclip className="h-4 w-4 text-blue-500" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">
          {file.filename}
        </p>
        {file.created_at && (
          <p className="text-xs text-gray-500">
            Subido el {parseDateToSpanishString(file.created_at)}
          </p>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>

      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onViewFile}
                disabled={isLoading}
                className={cn(
                  "flex h-11 w-11 sm:h-8 sm:w-8 items-center justify-center rounded-lg transition-colors",
                  "text-gray-400 hover:bg-gray-200 hover:text-gray-600",
                  isLoading && "opacity-50",
                )}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>Abrir archivo</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onDownloadFile}
                disabled={isLoading}
                className={cn(
                  "flex h-11 w-11 sm:h-8 sm:w-8 items-center justify-center rounded-lg transition-colors",
                  "text-gray-400 hover:bg-gray-200 hover:text-gray-600",
                  isLoading && "opacity-50",
                )}
              >
                <Download className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Descargar archivo</TooltipContent>
          </Tooltip>

          {showDelete && (
            <AlertDialog>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      className="flex h-11 w-11 sm:h-8 sm:w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent>Eliminar archivo</TooltipContent>
              </Tooltip>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminar archivo</AlertDialogTitle>
                  <AlertDialogDescription>
                    ¿Estás seguro que deseas eliminar este archivo? Esta acción
                    no se puede deshacer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onRemove}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </TooltipProvider>
    </div>
  );
};

export const UploadedFiles: React.FC = () => {
  const { activeRequirement, activeVault } = useAppSelector(
    (state) => state.vault,
  );
  const files = activeRequirement?.files || [];
  const removeFileMutation = useRemoveFile(activeVault?.id);

  const showDelete = activeRequirement?.status
    ? validDeleteStatus.includes(activeRequirement.status)
    : false;

  if (files.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {activeRequirement?.status === Status.Invalid && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm font-medium text-amber-900">
            Este requisito fue rechazado.
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Los archivos mostrados quedan como referencia, pero debes
            seleccionar y subir un archivo nuevo antes de reenviarlo.
          </p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          Archivos cargados
        </label>
        <span className="text-xs text-gray-500">
          {files.length} {files.length === 1 ? "archivo" : "archivos"}
        </span>
      </div>
      <div className="space-y-2">
        {files.map((file) => (
          <FileItem
            key={file.id}
            file={file}
            showDelete={showDelete}
            onRemove={() =>
              removeFileMutation.mutate(file.id, {
                onError: (error: any) => {
                  toast.error(
                    error?.response?.data?.detail ??
                      "No se pudo eliminar el archivo.",
                  );
                },
              })
            }
          />
        ))}
      </div>
    </div>
  );
};
